// Copyright (c) Docugami, Inc. All rights reserved.
import { Checkbox, ColumnActionsMode, DetailsHeader, DetailsListLayoutMode, IColumn, IDetailsHeaderProps, IDetailsHeaderStyles, IDetailsList, IDetailsListCheckboxProps, IDetailsRowProps, IDragDropEvents, IListProps, IRenderFunction, ITooltipHostProps, Icon, Link, ScrollablePane, SelectAllVisibility, Selection, SelectionMode, ShimmeredDetailsList, Sticky, StickyPositionType, ThemeProvider, TooltipHost, TooltipOverflowMode, mergeStyleSets } from "@fluentui/react";
import { OverflowTooltipText } from "components/common/OverflowText";
import 'components/file_management/presentation/styles/DocumentListViewStyles.scss';
import React, { MutableRefObject, Ref, forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import styled from 'styled-components';
import { useDgIcons } from "../../../hooks/dgIcons";
import { AppState } from "../../../store";
import { SectionalUI, userFontFamily } from "../../common/Fonts";
import { detailsListHeaderStyle, emptyGray, greyText, primaryTheme, secondaryTheme, themeBlue, themeWhite, transparentDetailsList } from "../../shell/presentation/AppStyles";
import { UnassignedDocSetId } from "../file_helpers";
import { IDocumentViewControls, IDocumentViewItem, PropertyCallbacks } from "./DocumentViewer";
import { FileWarningsIndicator } from "./FileWarningIndicators";

const DisabledRow = styled.div`
    opacity: .3;
`

const ItemText = styled(SectionalUI)`
    font-size: 14px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

const FileName = styled.span`
    display: flex;
    gap: 5px;
    font-family: ${userFontFamily};
    font-size: 16px;
    font-weight: 100;
    height: 100%;
    width: 100%;
`;

const OverflowWrapper = styled.div`
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const IconContainer = styled.div`
    position: relative;
`;

const IsNewVisual = styled.div`
    border-radius: 50%;
    width: 8px;
    height: 8px;
    background: ${themeBlue};
    position: absolute;
    top: -4px;
    left: -4px;
`

const RelativeRoot = styled.div`
    position: relative;
    height: 100%;
    width: 100%;
`

const ListViewTextContainer = styled.div<{ justifyContent?: string }>`
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    text-align: center;
`

export enum ListViewColumns {
    Icon = 1 << 0,
    Name = 1 << 1,
    DocSet = 1 << 2,
    DocStatus = 1 << 3,
    AddedOn = 1 << 4,
    Size = 1 << 5,
    DocSource = 1 << 6,
    All = ~0
}

export type IDocumentPageViewItem = IDocumentViewItem | null;

export interface DocumentListViewProps {
    isLoading?: boolean;
    items: IDocumentPageViewItem[];
    filter: ListViewColumns;
    isTransparent: boolean;
    singleSelection: boolean;
    selectedItemsChanged: (ids: string[]) => void;
    links?: PropertyCallbacks<IDocumentViewItem, string>;
    actions?: PropertyCallbacks<IDocumentViewItem, void>;
    validateActions?: PropertyCallbacks<IDocumentViewItem, boolean>;
    dragDropEvents?: IDragDropEvents;
    onSort?: (field: string | undefined, descending?: boolean) => void;
    sortableColumns?: (keyof IDocumentViewItem)[];
    listProps?: IListProps;
    disableSelectAll?: boolean;
}

const DocumentListView = (props: DocumentListViewProps, ref: Ref<IDocumentViewControls>): JSX.Element => {
    const [sortKey, setSortKey] = useState<string>();
    const [isSortedDescending, setIsSortedDescending] = useState<boolean>();

    const selectionRef = useRef<Selection<IDocumentViewItem>>(new Selection({
        onSelectionChanged: () => updateSelectedItems(),
        onItemsChanged: () => updateSelectedItems(),
        getKey: item => item.id,
        canSelectItem: item => !!item && !item.isDisabled
    }));

    const detailsListRef = useRef<IDetailsList>();
    const docSetsLookup = useSelector((state: AppState) => state.clusters.indexed);
    const dgIcons = useDgIcons();

    useImperativeHandle(ref, (): IDocumentViewControls => ({
        clearSelection: () => {
            selectionRef.current.setAllSelected(false);
        },

        selectAll: () => {
            if (!props.singleSelection) {
                selectionRef.current.setAllSelected(true);
            }
        },

        selectItems: (ids: string[], clearSelection?: boolean) => {
            const items = selectionRef.current.getItems();
            if (!!clearSelection) {
                selectionRef.current.setAllSelected(false);
            }

            if (ids.length === 0) {
                selectionRef.current.setAllSelected(false);
            }
            else if (props.singleSelection) {
                selectionRef.current.setAllSelected(false);
                selectionRef.current.setKeySelected(ids[0], true, false);
                const index = items.findIndex(x => x.id === ids[0]);
                if (index !== -1) {
                    detailsListRef.current!.focusIndex(index)
                }
            }
            else {
                for (const id of ids) {
                    if (!selectionRef.current.isKeySelected(id)) {
                        selectionRef.current.setKeySelected(id, true, false);
                    }
                }
            }
        }
    }));

    function updateSelectedItems(): void {
        const selectedItems = selectionRef.current.getSelection().filter(x => !!x).map(item => item.id);
        props.selectedItemsChanged(selectedItems);
    }

    const { onSort, sortableColumns } = props;
    const getColumnClickHandler = useCallback((key: keyof IDocumentViewItem) => {
        if (!!onSort && sortableColumns?.includes(key)) {
            return (ev: React.MouseEvent<HTMLElement>, column: IColumn) => {
                if (!column.fieldName) {
                    return;
                }

                let newKey: string | undefined = column.fieldName;
                let descending = undefined;
                if (sortKey === column.fieldName) {
                    descending = !isSortedDescending;
                    if (isSortedDescending === true) {
                        newKey = undefined;
                        descending = undefined
                    }
                } else {
                    descending = false;
                }

                setSortKey(newKey);
                setIsSortedDescending(descending);
                onSort(newKey, descending);
            }
        }
    }, [onSort, sortableColumns, sortKey, isSortedDescending])

    const columns = useMemo(() => {
        function getIcon(item: IDocumentViewItem): JSX.Element {
            return dgIcons.icon(
                item.icon.name,
                '',
                item.icon.height,
                item.icon.width,
                emptyGray,
                ''
            );
        }

        const getColumnActionsMode = (key: keyof IDocumentViewItem) => {
            return props.sortableColumns?.includes(key) ? ColumnActionsMode.clickable : ColumnActionsMode.disabled;
        }

        function getColumnAction<P extends keyof IDocumentViewItem>(key: P, item: IDocumentViewItem): ((item: IDocumentViewItem) => void) | null {
            const action = props.actions?.[key] as (item: IDocumentViewItem) => void;
            const validate = props.validateActions?.[key];
            if (!!action) {
                if (!!validate && !validate(item)) {
                    return null;
                }

                return action;
            }

            return null;
        }

        function getColumnLink<P extends keyof IDocumentViewItem>(key: P, item: IDocumentViewItem): string | null {
            const link = props.links?.[key]?.(item) as string;
            const validate = props.validateActions?.[key];
            if (!!link) {
                if (!!validate && !validate(item)) {
                    return null;
                }

                return link;
            }

            return null;
        }

        const columns: IColumn[] = [];
        if (props.filter & ListViewColumns.Icon) {
            const key = 'column' + columns.length;
            columns.push(
                {
                    key: key,
                    name: 'ItemIcon',
                    className: 'fileIconCell',
                    iconClassName: '',
                    iconName: '',
                    isIconOnly: true,
                    minWidth: 30,
                    maxWidth: 30,
                    columnActionsMode: 0,
                    onRender: (item: IDocumentViewItem) => {
                        return (
                            <IconContainer>
                                {getIcon(item)}
                                {!!item.isNew && <IsNewVisual />}
                            </IconContainer>
                        );
                    }
                }
            );
        }

        if (props.filter & ListViewColumns.Name) {
            const key = 'column' + columns.length;
            columns.push(
                {
                    key: key,
                    name: 'Name',
                    minWidth: 200,
                    isResizable: true,
                    isRowHeader: true,
                    data: 'string',
                    isPadded: true,
                    fieldName: 'name',
                    columnActionsMode: getColumnActionsMode('name'),
                    onColumnClick: getColumnClickHandler('name'),
                    isSorted: sortKey === 'name',
                    isSortedDescending: sortKey === 'name' ? isSortedDescending : undefined,
                    styles: mergeStyleSets(detailsListHeaderStyle, {
                        cellTitle: {
                            width: '100%'
                        }
                    }),
                    onRender: (item: IDocumentViewItem) => {
                        const action = getColumnAction("name", item);
                        const nameElement = <TooltipHost content={item.name} overflowMode={TooltipOverflowMode.Parent}>{item.name}</TooltipHost>;
                        let content: JSX.Element;
                        if (!!action) {
                            content = <Link className="ms-Link" href="" onClick={() => action(item)} style={{ color: "black", flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nameElement}</Link>
                        } else {
                            content = <OverflowWrapper>{nameElement}</OverflowWrapper>
                        }

                        return (
                            <FileName data-selection-disabled={true}>
                                {content}
                                {!!item.fileWarnings && <FileWarningsIndicator warnings={item.fileWarnings} />}
                            </FileName>
                        )
                    }
                }
            );
        }

        if (props.filter & ListViewColumns.DocSet) {
            const key = 'column' + columns.length;
            columns.push(
                {
                    key: key,
                    name: 'Document Set',
                    fieldName: 'docSet',
                    minWidth: 200,
                    maxWidth: 500,
                    isResizable: true,
                    isRowHeader: true,
                    data: 'string',
                    isPadded: true,
                    columnActionsMode: getColumnActionsMode('docSet'),
                    onColumnClick: getColumnClickHandler('docSet'),
                    isSorted: sortKey === 'docSet',
                    isSortedDescending: sortKey === 'docSet' ? isSortedDescending : undefined,
                    styles: detailsListHeaderStyle,
                    onRender: (item: IDocumentViewItem) => {
                        const docSetLabel = <OverflowTooltipText content={docSetsLookup[item.docSet]?.clusterInfo.label}>{docSetsLookup[item.docSet]?.clusterInfo.label}</OverflowTooltipText>
                        if (!!item.docSet && item.docSet !== UnassignedDocSetId && item.docSet in docSetsLookup) {
                            let content;
                            const link = getColumnLink("docSet", item);
                            if (!!link) {
                                content = <Link className="ms-Link" href={link}>{docSetLabel}</Link>
                            }
                            else {
                                content = <ItemText style={{ color: "black" }}>{docSetLabel}</ItemText>
                            }

                            return (
                                <ListViewTextContainer justifyContent='start'>
                                    {content}
                                </ListViewTextContainer>
                            );
                        }
                    }
                }
            );
        }

        if (props.filter & ListViewColumns.DocStatus) {
            const key = 'column' + columns.length;
            columns.push(
                {
                    key: key,
                    name: 'Status',
                    fieldName: 'status',
                    minWidth: 110,
                    maxWidth: 200,
                    isResizable: true,
                    isRowHeader: true,
                    data: 'string',
                    isPadded: true,
                    columnActionsMode: getColumnActionsMode('status'),
                    onColumnClick: getColumnClickHandler('status'),
                    isSorted: sortKey === 'status',
                    isSortedDescending: sortKey === 'status' ? isSortedDescending : undefined,
                    styles: detailsListHeaderStyle,
                    onRender: (item: IDocumentViewItem) => {
                        return getStatus(item)
                    }
                }
            )
        }

        if (props.filter & ListViewColumns.DocSource) {
            const key = 'column' + columns.length;
            columns.push(
                {
                    key: key,
                    name: 'Source',
                    fieldName: 'docSource',
                    minWidth: 100,
                    maxWidth: 500,
                    isResizable: true,
                    isRowHeader: true,
                    data: 'string',
                    isPadded: true,
                    columnActionsMode: getColumnActionsMode('docSource'),
                    onColumnClick: getColumnClickHandler('docSource'),
                    isSorted: sortKey === 'docSource',
                    isSortedDescending: sortKey === 'socSource' ? isSortedDescending : undefined,
                    styles: detailsListHeaderStyle,
                    onRender: (item: IDocumentViewItem) => {
                        return <ListViewTextContainer><ItemText>{item.docSource}</ItemText></ListViewTextContainer>;
                    }
                }
            )
        }

        if (props.filter & ListViewColumns.AddedOn) {
            const key = 'column' + columns.length;
            columns.push(
                {
                    key: key,
                    name: 'Added',
                    fieldName: 'createdAt',
                    minWidth: 80,
                    maxWidth: 200,
                    isResizable: true,
                    isRowHeader: true,
                    data: 'string',
                    isPadded: true,
                    columnActionsMode: getColumnActionsMode('createdAt'),
                    styles: detailsListHeaderStyle,
                    onRender: (item: IDocumentViewItem) => {
                        return <ListViewTextContainer><ItemText>{item.createdAt}</ItemText></ListViewTextContainer>;
                    }
                }
            );
        }

        if (props.filter & ListViewColumns.Size) {
            const key = 'column' + columns.length;
            columns.push(
                {
                    key: key,
                    name: 'Size',
                    fieldName: 'size',
                    minWidth: 50,
                    maxWidth: 100,
                    isResizable: true,
                    isRowHeader: true,
                    data: 'string',
                    isPadded: true,
                    columnActionsMode: getColumnActionsMode('size'),
                    styles: detailsListHeaderStyle,
                    onRender: (item: IDocumentViewItem) => {
                        return <ListViewTextContainer><ItemText>{item.size}</ItemText></ListViewTextContainer>
                    }
                }
            )
        }

        return columns;
    }, [docSetsLookup, getColumnClickHandler, dgIcons, props.filter, props.sortableColumns, props.actions, props.links, props.validateActions, sortKey, isSortedDescending])

    /**
     * Returns appropriate file status message, corresponding icon, and tooltip with further information
     * @param item the FileState to evaluate a status for
     */
    function getStatus(item: IDocumentViewItem): JSX.Element | null {
        if (!item.status) {
            return null;
        }

        return (
            <ListViewTextContainer justifyContent='start'>
                <TooltipHost content={item.status.tooltip} styles={{ root: { display: 'flex' } }}>
                    {
                        !!item.status.iconProps ?
                            <Icon iconName={item.status.iconProps.iconName} style={{ color: item.status.color, marginRight: "5px" }}></Icon> :
                            <Icon iconName="StatusCircleInner" style={{ color: item.status.color, marginRight: "5px" }}></Icon>
                    }
                    <ItemText style={{ fontSize: '12px', fontWeight: 500 }}>{item.status.message.toLocaleUpperCase()}</ItemText>
                </TooltipHost>
            </ListViewTextContainer>
        );
    }

    function renderCheckbox(itemCheckedProps?: IDetailsListCheckboxProps): JSX.Element | null {
        if (!itemCheckedProps) {
            return null;
        }

        let size = "16px";
        let checkmarkSize = "12px";
        return (
            <Checkbox
                inputProps={{ 'data-selection-disabled': true } as any}
                styles={{
                    checkbox: { height: size, width: size },
                    text: { lineHeight: size },
                    checkmark: { fontSize: checkmarkSize, color: themeWhite }
                }}
                checked={itemCheckedProps.checked}
            />
        )
    }

    const onRenderDetailsHeader: IRenderFunction<IDetailsHeaderProps> = (headerProps?: IDetailsHeaderProps): JSX.Element | null => {
        if (!headerProps) {
            return null;
        }

        const headerDarkBorderStyle: Partial<IDetailsHeaderStyles> = {
            root: {
                borderTop: `1px solid ${greyText}`,
                borderBottom: `1px solid ${greyText}`,
                paddingTop: 0,
                marginTop: '16px'
            }
        }

        return (
            <Sticky stickyPosition={StickyPositionType.Header} isScrollSynced>
                <DetailsHeader
                    {...headerProps}
                    styles={headerDarkBorderStyle}
                    selectAllVisibility={!props.disableSelectAll ? SelectAllVisibility.visible : SelectAllVisibility.hidden}
                    onRenderDetailsCheckbox={() => {
                        return <Checkbox
                            inputProps={{ 'data-selection-disabled': true } as any}
                            indeterminate={selectionRef.current.getSelectedCount() > 0 && !selectionRef.current.isAllSelected()}
                            onChange={(ev?: React.FormEvent<HTMLElement | HTMLInputElement> | undefined, checked?: boolean | undefined) => {
                                selectionRef.current.setAllSelected(!!checked);
                            }}
                        />
                    }}
                    onRenderColumnHeaderTooltip={renderColumnHeader} />
            </Sticky>
        );
    };

    function onRenderRow(props?: IDetailsRowProps, defaultRender?: (props?: IDetailsRowProps) => JSX.Element | null) {
        if (!props || !defaultRender) {
            return null;
        }

        const item = props.item as IDocumentViewItem;
        let row: JSX.Element | null = null;
        if (!!item.isDisabled) {
            row = (
                <TooltipHost calloutProps={{ theme: primaryTheme }} content={!!props.item.isDisabled ? props.item.disabledToolTip : undefined}>
                    <DisabledRow>
                        {defaultRender(props)}
                    </DisabledRow>
                </TooltipHost>
            )
        }
        else {
            row = defaultRender(props);
        }

        return row;
    }

    const renderColumnHeader: IRenderFunction<ITooltipHostProps> = (props) => {
        return <SectionalUI style={{ display: 'flex', justifyContent: 'center' }}>{props?.children}</SectionalUI>
    }

    return (
        <RelativeRoot>
            <ScrollablePane>
                <ThemeProvider theme={props.isTransparent ? transparentDetailsList : primaryTheme}>
                    <ShimmeredDetailsList
                        componentRef={detailsListRef as MutableRefObject<IDetailsList>}
                        items={props.items}
                        theme={secondaryTheme}
                        compact={false}
                        columns={columns}
                        listProps={props.listProps}
                        selectionMode={props.singleSelection ? SelectionMode.single : SelectionMode.multiple}
                        setKey="set"
                        layoutMode={DetailsListLayoutMode.justified}
                        isHeaderVisible={true}
                        dragDropEvents={props.dragDropEvents}
                        enableShimmer={props.isLoading}
                        removeFadingOverlay={true}
                        shimmerLines={20}
                        //@ts-ignore
                        selection={selectionRef.current}
                        selectionPreservedOnEmptyClick={true}
                        enterModalSelectionOnTouch={true}
                        onRenderCheckbox={renderCheckbox}
                        onRenderDetailsHeader={onRenderDetailsHeader}
                        onRenderRow={onRenderRow}
                    />
                </ThemeProvider>
            </ScrollablePane>
        </RelativeRoot>
    );
}

export default forwardRef(DocumentListView);